import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp } from 'lucide-react';

import Button from '../../common/Button';
import { type TypeScenarioListDisplay } from '../../common/dataTypes';
import Loading from '../../common/Loading';
import { useGetDistinguishModel } from '../../hooks/tanstack/useUtilsHook';
import { type ModelType } from '../../types';
import cn from '../../utils/cn';

import ScenarioMenuDropdown from './ScenarioMenuDropdown';

type DataRow = {
  id: string;
  inputTable: string;
  supportVectors: boolean;
  isPivot: boolean;
  isBase: boolean;
  isLocked: boolean;
  scenarios: TypeScenarioListDisplay['groups'][0]['data_table_schemas'][0]['scenarios'];
  vectors?: string[];
};

type ScenarioGroupInputTableProps = {
  groupName: string;
  isLoading?: boolean;
  dataTableSchemas: TypeScenarioListDisplay['groups'][0]['data_table_schemas'];
};

const columnHelper = createColumnHelper<DataRow>();

const ScenarioGroupInputTable = ({
  groupName,
  isLoading: isLoadingDataTableSchemas,
  dataTableSchemas,
}: ScenarioGroupInputTableProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const { model, type } = useGetDistinguishModel();

  const data = useMemo(
    () =>
      dataTableSchemas && model
        ? dataTableSchemas.map((dataTable) => {
            const { name, display_name, scenarios, support_vectors, is_pivot } =
              dataTable;

            return {
              id: name,
              supportVectors: support_vectors,
              isPivot: is_pivot,
              isLocked: model.is_locked,
              isBase: model.is_base,
              inputTable: display_name ?? 'Unknown',
              scenarios,
            };
          })
        : [],
    [dataTableSchemas, model]
  );

  const handleSetIsLoading = (loading: boolean) => {
    setIsLoading(loading);
  };

  const handleOpenEdit = ({
    dataTableName,
    activeScenarioId,
    type,
  }: {
    dataTableName: string;
    activeScenarioId: string;
    type: ModelType;
  }) => {
    navigate(
      `input-table/${dataTableName}?scenario_id=${activeScenarioId}&type=${type}`
    );
  };

  const table = useReactTable<DataRow>({
    data,
    columns: [
      columnHelper.accessor('inputTable', {
        cell: (info) => (
          <div className="py-2 text-start w-[300px]">
            <p className="w-full whitespace-nowrap">
              {info.getValue() ?? '--'}
            </p>
          </div>
        ),
        size: 300,
        header: () => (
          <div className="flex w-[300px] items-center gap-2">
            <p>{groupName}</p>
          </div>
        ),
      }),
      columnHelper.accessor('scenarios', {
        id: 'scenarios',
        cell: (info) => {
          const {
            id: dataTableName,
            inputTable: dataTableDisplayName,
            supportVectors,
            isBase,
            isPivot,
            isLocked,
            scenarios,
          } = info.row.original;

          const { isDefault, type } = useGetDistinguishModel();

          const activeScenario = useMemo(
            () => scenarios?.find((scenario) => scenario.is_active),
            [scenarios]
          );

          const defaultScenario = useMemo(
            () =>
              activeScenario
                ? {
                    id: activeScenario.id,
                    label: activeScenario.name,
                  }
                : {
                    id: 0,
                    label: 'BASE',
                  },
            [activeScenario]
          );

          return (
            <div>
              {supportVectors && !isBase && isDefault && !isLocked ? (
                <ScenarioMenuDropdown
                  dataTableName={dataTableName}
                  dataTableDisplayName={dataTableDisplayName}
                  defaultValue={defaultScenario}
                  isPivot={isPivot}
                  handleSetIsLoading={handleSetIsLoading}
                  scenarios={scenarios}
                />
              ) : (
                <p
                  onClick={() =>
                    handleOpenEdit({
                      dataTableName,
                      activeScenarioId: defaultScenario.id.toString(),
                      type,
                    })
                  }
                  className="whitespace-normal w-full"
                >
                  {defaultScenario.label}
                </p>
              )}
            </div>
          );
        },
        size: 150,
        header: () => <div>Scenario</div>,
      }),
      columnHelper.accessor('vectors', {
        id: 'vectors',
        cell: (info) => {
          const { scenarios, supportVectors, isBase } = info.row.original;

          const activeScenario = scenarios?.find(
            (scenario) => scenario.is_active
          );

          const scenarioVectors = activeScenario?.vectors;

          return (
            <div>
              {supportVectors && !isBase ? (
                <div className="flex justify-between">
                  <ul className="list-disc px-6">
                    {scenarioVectors?.length
                      ? scenarioVectors.map((vector) => <li>{vector.name}</li>)
                      : '--'}
                  </ul>
                </div>
              ) : (
                <div className="w-full h-full opacity-0" aria-hidden="true">
                  " "
                </div>
              )}
            </div>
          );
        },
        size: 150,
        header: () => <div>Vectors</div>,
      }),
    ],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  const navigateToConfiguration = () => {
    searchParams.set('tab', 'configuration');
    setSearchParams(searchParams);
  };

  return (
    <div>
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="!rounded-md border border-[#E4E7EC] bg-[#F8F8F8]"
            >
              {headerGroup.headers.map((header, index) => (
                <th
                  key={header.id}
                  className={cn(
                    'border border-[#E4E7EC] px-6 py-2 text-start text-sm text-[#4D4D4D]',
                    {
                      'w-[300px]': index === 0,
                      'w-[250px]': index === 1,
                      'w-[200px]': index === 2,
                    }
                  )}
                >
                  <button
                    type="button"
                    onClick={header.column.getToggleSortingHandler()}
                    className="flex gap-1 items-start"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: <ArrowUp size={20} />,
                      desc: <ArrowDown size={20} />,
                    }[header.column.getIsSorted() as string] ?? null}
                  </button>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="relative">
          {(isLoading || isLoadingDataTableSchemas) && (
            <Loading className="h-full absolute bg-gray-100" />
          )}
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="cursor-pointer py-2">
              {row.getVisibleCells().map((cell, index) => {
                const dataTableName = row.original.id;

                const activeScenario = row.original.scenarios?.find(
                  (scenario) => scenario.is_active
                );

                const activeScenarioId = activeScenario?.id.toString() ?? '0';

                return (
                  <td
                    onClick={() => {
                      index !== 1 &&
                        handleOpenEdit({
                          dataTableName,
                          activeScenarioId,
                          type,
                        });
                    }}
                    key={cell.id}
                    className={cn('border border-[#E4E7EC] px-6 text-[#333]', {
                      'w-[300px]': index === 0,
                      'w-[200px]': index === 1,
                      'w-[150px]': index === 2,
                    })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!table.getRowModel().rows.length && !isLoading && (
        <div className="flex justify-center border border-t-0 border-[#E4E7EC] py-20">
          <p className="text-[#999]">
            No Expenses selected. Please go go the &nbsp;
            <Button variant="link_blue" onClick={navigateToConfiguration}>
              Configuration
            </Button>
            &nbsp; page to set your expenses.
          </p>
        </div>
      )}
    </div>
  );
};

export default ScenarioGroupInputTable;
